import { Backdrop } from '@mui/material'
import { Formik, FormikHelpers } from 'formik'
import { isNull } from 'lodash'
import { createPortal } from 'react-dom'
import { tss } from 'tss-react/mui'
import * as Yup from 'yup'
import { RightPanelOperation } from '~/components/table-settings/constants'
import { Button } from '~/components/ui/atoms/button/Button'
import { Modal } from '~/components/ui/molecules/modal/Modal'
import { useTypedTranslation } from '../../common/hooks/useTypedTranslation'
import {
  TableColumnFormTableColumn,
  useTableColumnForm,
} from '../table-settings/TableColumnFormProvider'
import { SettingsPageTable } from '../tables/types'
import { Tooltip } from '../ui/atoms/tooltip/Tooltip'
import { CreateUpdateTableColumnForm } from './CreateUpdateTableColumnForm'
import { ColumnFormValues, DEFAULT_COLUMN_VALUES } from './types'

interface CreateTableColumnDialogProps {
  onClose: () => void
  table: Pick<SettingsPageTable, 'id' | 'name' | 'columns' | 'hasRows'>
  parentTable?: Pick<SettingsPageTable, '__typename' | 'id'> | null
  order?: number
  // All the columns on the table
  columns: ColumnFormValues[]
}

/**
 * Renders the dialog for creating a table column
 */
export function CreateTableColumnDialog({
  onClose,
  table,
  parentTable,
  order,
  columns,
}: CreateTableColumnDialogProps): JSX.Element | null {
  const { formulaError, setFormulaError, handleCreate, getValidationSchema } = useTableColumnForm()
  const { classes } = useStyles()
  const { t } = useTypedTranslation()

  const handleSubmit = (
    values: ColumnFormValues,
    {
      setSubmitting,
      resetForm,
    }: Pick<FormikHelpers<ColumnFormValues>, 'setSubmitting' | 'resetForm'>,
  ) => {
    // Wrap operations in a promise chain
    new Promise((resolve, reject) => {
      // Dialog is only used for creating a table column
      handleCreate(values).then(resolve, reject)
    })
      .then(() => {
        // Success path: reset form, stop submitting state, close modal
        resetForm()
        setSubmitting(false)
        onClose()
      })
      .catch(() => {
        setSubmitting(false)
      })
  }

  const canProvideDefaultValue = table.hasRows

  // DisablePortal is true because we require the create update table column form to be under
  // the DOM hierarchy of the form.  This, however, causes the backdrop to not appear over
  // the entire screen when the modal is open. To prevent this, we are creating a portal
  // that wraps around the form and a separate backdrop component and we are hiding the modal's
  // own backdrop.  The z-index of the backdrop is set to the default MUI modal z-index - 1 so it
  // covers the top nagivation bars
  return createPortal(
    <>
      <Backdrop className={classes.backdrop} onClick={onClose} open />
      <Formik
        key="new"
        enableReinitialize
        initialValues={DEFAULT_COLUMN_VALUES}
        onSubmit={handleSubmit}
        validate={(values) => {
          // The default value is required if creating a new column and the existing table has
          // rows and the new column is required
          const defaultValueIsRequired = canProvideDefaultValue && !values.isNullable
          // Since some columns do not have an id since they have not been created yet, set with
          // the temporary id before the check for valid name
          const columnsWithId: TableColumnFormTableColumn[] = columns.map((column) => {
            return {
              ...column,
              __typename: 'TableColumn',
              id: column.id ?? column.tempId, // Set id to tempId if id is undefined
              hasNonNullCells: false,
              hasNullCells: false,
              formula: null,
            }
          })
          const schema = getValidationSchema(defaultValueIsRequired, columnsWithId)

          return schema
            .validate(values, { abortEarly: false })
            .then(() => ({})) // Success, return empty errors
            .catch((err: Yup.ValidationError) => {
              const errors: { [key: string]: string } = {}
              err.inner.forEach((error) => {
                if (error.path && !errors[error.path]) {
                  errors[error.path] = error.message
                }
              })
              return errors
            })
        }}
        validateOnMount
      >
        {({ isValid, isSubmitting, handleSubmit, resetForm, errors, values }) => {
          let tableNameDisplay = t('table_column.new_table')
          tableNameDisplay = table.name

          const modalTitle = `Add column ${values.name} to ${tableNameDisplay}`

          let tooltipTitle = Object.values(errors).join(', ')
          if (formulaError) {
            if (tooltipTitle) {
              tooltipTitle += ', '
            }
            tooltipTitle += formulaError
          }

          return (
            <form onSubmit={handleSubmit}>
              <Modal
                actions={
                  <Tooltip arrow title={tooltipTitle}>
                    <div>
                      <Button
                        disabled={!isValid || isSubmitting || !isNull(formulaError)}
                        size="small"
                        type="submit"
                        variant="secondary"
                      >
                        {t('table_column.save')}
                      </Button>
                    </div>
                  </Tooltip>
                }
                disablePortal
                fullWidth
                hideBackdrop
                onClose={() => {
                  onClose()
                  resetForm()
                }}
                open
                title={modalTitle}
              >
                <CreateUpdateTableColumnForm
                  columnHasNonNullCells={false}
                  columnHasNullCells={false}
                  formulaError={formulaError}
                  operation={RightPanelOperation.CREATE}
                  order={order}
                  parentTableId={parentTable?.id}
                  setFormulaError={setFormulaError}
                  showButtons={false}
                  table={table}
                />
              </Modal>
            </form>
          )
        }}
      </Formik>
    </>,
    document.body,
  )
}

const useStyles = tss.create(({ theme }) => ({
  backdrop: {
    zIndex: theme.zIndex.modal,
  },
}))
